import { useCallback, useEffect, useState } from "react";
import { rqlBuilder } from "@extrahorizon/javascript-sdk";

import { getExhClient, isExhClientInitialized } from "lib/extrahorizon/client";
import { ExhSettingsDocument } from "types/measurement";

interface UseExhSettingsDocumentResult<TData> {
  document: ExhSettingsDocument<TData> | undefined;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches the latest document for a settings schema (e.g.
 * `measurement-wbc-settings` or `measurement-fbc-settings`), sorted by
 * `creationTimestamp` descending. 401/403 are treated as "no document"
 * because the schema's `readMode: permissionRequired` enforces visibility.
 */
export const useExhSettingsDocument = <TData>(schemaName: string | null): UseExhSettingsDocumentResult<TData> => {
  const [document, setDocument] = useState<ExhSettingsDocument<TData> | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!schemaName) {
      setLoading(false);
      setDocument(undefined);
      return;
    }

    if (!isExhClientInitialized()) {
      setLoading(false);
      setError(new Error("ExtraHorizon client not initialized"));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const client = getExhClient();
      const rql = rqlBuilder().sort("-creationTimestamp").limit(1).build();
      const response = await client.data.documents.find(schemaName, { rql });
      const docs = (response?.data ?? []) as ExhSettingsDocument<TData>[];
      setDocument(docs[0]);
    } catch (err) {
      const status = (err as { status?: number } | null)?.status;
      if (status === 401 || status === 403) {
        setDocument(undefined);
        return;
      }
      const e = err instanceof Error ? err : new Error("Failed to fetch settings document");
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [schemaName]);

  useEffect(() => {
    load();
  }, [load]);

  return { document, loading, error, refetch: load };
};
